// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { ROUTES } from '@/constants/routes'
import { apiClient } from '@/lib/api/client'
import { friendlyError } from '@/lib/utils/errorHandler'

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    if (!token.trim()) {
      setError('This reset link is missing a token. Open the link from your email again.')
      return
    }
    setLoading(true)
    try {
      await apiClient.post('/v1/auth/reset-password', { token: token.trim(), newPassword: password })
      setDone(true)
    } catch (err) {
      setError(friendlyError(err))
    } finally {
      setLoading(false)
    }
  }

  if (!token.trim() && !done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <img src="/etapsky_mark.svg" alt="Etapsky" className="h-10 w-10 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-ink">Invalid reset link</h1>
            <p className="mt-1 text-sm text-ink-muted">Use the link from your password reset email.</p>
          </div>
          <div className="card p-6">
            <Alert variant="error" title="Missing token">
              If the link expired, request a new one from the sign-in page.
            </Alert>
          </div>
          <p className="mt-4 text-center text-sm text-ink-muted">
            <Link to={ROUTES.FORGOT_PASSWORD} className="text-brand hover:underline">Request reset</Link>
            {' · '}
            <Link to={ROUTES.LOGIN} className="text-brand hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/etapsky_mark.svg" alt="Etapsky" className="h-10 w-10 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-ink">Choose a new password</h1>
          <p className="mt-1 text-sm text-ink-muted">8+ characters, one uppercase letter, one number</p>
        </div>

        <div className="card p-6">
          {done ? (
            <Alert variant="success" title="Password updated">
              You can sign in with your new password.
            </Alert>
          ) : (
            <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
              {error && <Alert variant="error">{error}</Alert>}
              <Input
                label="New password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={8}
                autoComplete="new-password"
              />
              <Input
                label="Confirm password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
                required
                minLength={8}
                autoComplete="new-password"
              />
              <Button type="submit" className="w-full" loading={loading}>
                Update password
              </Button>
            </form>
          )}
        </div>

        <p className="mt-4 text-center text-sm text-ink-muted">
          <Link to={ROUTES.LOGIN} className="text-brand hover:underline">← Back to sign in</Link>
        </p>
      </div>
    </div>
  )
}
